"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { BedDouble, Users } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { StayRoomCatalogEntry } from "@/lib/stay/roomCatalog";

const formatPrice = (price: number) => `R${Math.round(price).toLocaleString("en-ZA")}`;

const MAX_THUMBS = 6;
const COMPACT_ROWS_FROM = 3;
const ROWS_COLLAPSED_COUNT = 5;
const BADGE_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Guest-facing "Rooms" section for multi-room Stay properties.
 *
 * Room presentation scales with room count (owner decision, 2026-11-04):
 * 1-2 rooms keep the photo-led cards that earn their space; 3-5 rooms render
 * as compact rows; 6+ rooms render as rows with a "Show all" expansion, so a
 * big host never becomes a wall of tiles.
 *
 * Every room card carries an availability badge — "Free your dates" when the
 * room is fully open for the next 90 nights, otherwise "X nights free in the
 * next 90" (owner-locked copy, Part B of the 2027 brief). The badge is
 * DISPLAY-ONLY: it reads the same public per-room busy data the booking
 * calendar already uses (/api/stays/rooms, which computes busyRanges
 * server-side, house cascade included) and shows free-night capacity. The
 * calendar inside the booking form stays the single deep availability
 * surface — no duplicate paging calendar here.
 */
export function StayRoomsSection({
  rooms,
  profileId,
}: {
  rooms: StayRoomCatalogEntry[];
  profileId?: string;
}) {
  const [openPhoto, setOpenPhoto] = useState<{ roomName: string; urls: string[]; index: number } | null>(null);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [roomBusyRanges, setRoomBusyRanges] = useState<Record<string, Array<{ start: string; end: string }>> | null>(null);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    fetch(`/api/stays/rooms?profileId=${encodeURIComponent(profileId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || !Array.isArray(data.rooms)) return;
        const busy: Record<string, Array<{ start: string; end: string }>> = {};
        for (const room of data.rooms) {
          if (room && typeof room.id === "string" && Array.isArray(room.busyRanges)) {
            busy[room.id] = room.busyRanges;
          }
        }
        setRoomBusyRanges(busy);
      })
      .catch(() => {
        /* badge is display-only; silently show no badge */
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const freeNightsByRoom = (() => {
    if (!roomBusyRanges) return null;
    const windowStart = new Date();
    windowStart.setUTCHours(0, 0, 0, 0);
    const windowStartMs = windowStart.getTime();
    const result: Record<string, number> = {};
    for (const [roomId, ranges] of Object.entries(roomBusyRanges)) {
      const busy = new Set<number>();
      for (const range of ranges) {
        const start = Date.parse(`${range.start}T00:00:00Z`);
        const end = Date.parse(`${range.end}T00:00:00Z`);
        if (!Number.isFinite(start) || !(end > start)) continue;
        const clippedStart = Math.max(start, windowStartMs);
        const clippedEnd = Math.min(end, windowStartMs + BADGE_WINDOW_DAYS * DAY_MS);
        for (let t = clippedStart; t < clippedEnd; t += DAY_MS) busy.add(t);
      }
      result[roomId] = BADGE_WINDOW_DAYS - busy.size;
    }
    return result;
  })();

  const withPhotos = rooms.filter((room) => room.photoUrls.length > 0);
  if (withPhotos.length === 0) return null;

  const compactRows = withPhotos.length >= COMPACT_ROWS_FROM;
  const visibleRows = compactRows && withPhotos.length > ROWS_COLLAPSED_COUNT && !showAllRooms
    ? withPhotos.slice(0, ROWS_COLLAPSED_COUNT)
    : withPhotos;

  const badge = (roomId: string) => {
    const free = freeNightsByRoom ? freeNightsByRoom[roomId] : null;
    if (free === null || free === undefined) return null;
    const fullyFree = free >= BADGE_WINDOW_DAYS;
    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
          fullyFree
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : "border-slate-200 bg-slate-100 text-slate-700"
        }`}
      >
        {fullyFree ? "Free your dates" : `${free} nights free in the next 90`}
      </span>
    );
  };

  // Send the picked room into the booking form and take the guest there.
  const bookThisRoom = (roomId: string) => {
    window.dispatchEvent(new CustomEvent("zamzi:select-room", { detail: { roomId } }));
    document.getElementById("stay-booking-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="mt-8" aria-label="Rooms at this property">
      <h2 className="text-xl font-bold">Choose your room</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Every room books separately with its own calendar and its own price — pick the one that suits your stay.
      </p>
      {compactRows ? (
        <div className="mt-4 flex flex-col gap-2">
          {visibleRows.map((room) => (
            <div
              key={room.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
            >
              <button
                type="button"
                className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-lg"
                onClick={() => setOpenPhoto({ roomName: room.roomName, urls: room.photoUrls, index: 0 })}
                aria-label={`View photos of ${room.roomName}`}
              >
                <Image
                  src={room.photoUrls[0]}
                  alt={room.roomName}
                  fill
                  sizes="72px"
                  className="object-cover"
                />
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{room.roomName}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {room.sleeps ? (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" /> Sleeps {room.sleeps}
                    </span>
                  ) : null}
                  {room.pricePerNight !== null && (
                    <span className="font-semibold text-primary">
                      From {formatPrice(room.pricePerNight)} / night
                    </span>
                  )}
                </p>
                <div className="mt-1">{badge(room.id)}</div>
              </div>
              <Button size="sm" className="shrink-0" onClick={() => bookThisRoom(room.id)}>
                Book this room
              </Button>
            </div>
          ))}
          {withPhotos.length > ROWS_COLLAPSED_COUNT && (
            <Button variant="ghost" size="sm" className="self-start" onClick={() => setShowAllRooms((v) => !v)}>
              {showAllRooms ? "Show fewer rooms" : `Show all ${withPhotos.length} rooms`}
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {withPhotos.map((room) => {
            const shown = room.photoUrls.slice(0, MAX_THUMBS);
            const extraCount = room.photoUrls.length - shown.length;
            return (
              <Card key={room.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <button
                    type="button"
                    className="group relative block aspect-video w-full"
                    onClick={() => setOpenPhoto({ roomName: room.roomName, urls: room.photoUrls, index: 0 })}
                    aria-label={`View photos of ${room.roomName}`}
                  >
                    <Image
                      src={shown[0]}
                      alt={room.roomName}
                      fill
                      sizes="(max-width: 640px) 100vw, 50vw"
                      className="object-cover transition duration-300 group-hover:scale-[1.02]"
                    />
                    {extraCount > 0 && (
                      <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                        +{extraCount} more
                      </span>
                    )}
                  </button>
                  <div className="p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-sm font-semibold">{room.roomName}</p>
                      {room.pricePerNight !== null && (
                        <p className="shrink-0 text-sm font-semibold text-primary">
                          From {formatPrice(room.pricePerNight)} <span className="font-normal text-muted-foreground">/ night</span>
                        </p>
                      )}
                    </div>
                    {room.sleeps ? (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="h-3 w-3" /> Sleeps {room.sleeps}
                      </p>
                    ) : null}
                    {badge(room.id)}
                    {shown.length > 1 && (
                      <div className="mt-2 flex gap-1.5">
                        {shown.slice(1).map((url, index) => (
                          <button
                            key={url}
                            type="button"
                            className="relative h-12 w-16 shrink-0 overflow-hidden rounded border border-slate-200"
                            onClick={() => setOpenPhoto({ roomName: room.roomName, urls: room.photoUrls, index: index + 1 })}
                            aria-label={`View photo ${index + 2} of ${room.roomName}`}
                          >
                            <Image src={url} alt={`${room.roomName} photo ${index + 2}`} fill sizes="64px" className="object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    <Button size="sm" className="mt-3 w-full" onClick={() => bookThisRoom(room.id)}>
                      Book this room
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={openPhoto !== null}
        onOpenChange={(next) => {
          if (!next) setOpenPhoto(null);
        }}
      >
        {openPhoto ? (
          <DialogContent className="max-w-4xl p-0">
            <DialogHeader className="px-5 pt-4">
              <DialogTitle className="flex items-center gap-2">
                <BedDouble className="h-4 w-4" />
                {openPhoto.roomName}
              </DialogTitle>
              <DialogDescription>
                Photo {openPhoto.index + 1} of {openPhoto.urls.length}
              </DialogDescription>
            </DialogHeader>
            <div className="relative aspect-[4/3] max-h-[70dvh] w-full bg-black">
              <Image
                src={openPhoto.urls[openPhoto.index]}
                alt={`${openPhoto.roomName} photo ${openPhoto.index + 1}`}
                fill
                sizes="90vw"
                className="object-contain"
              />
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </section>
  );
}
